import { computed, watch, nextTick, onMounted, onBeforeUnmount, useHost } from "vue";
import { useComponentContext } from "composables/useComponentContext";
import { useStateStore } from "composables/useStateStore";
import { useSharedState } from "composables/useSharedState";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { setDataAttr } from "@/runtime/shared/attrs";
import type { AccordionProps, AccordionItemProps } from "./accordion.types";
import { accordionRootClasses } from "./accordion.styles";

const ACCORDION_ITEM_TYPE = "AccordionItem";

/** Registry mapping accordion UIDs to their toggleItem method. */
const accordionToggleRegistry = new Map<string | number, (itemUid: string | number, newOpen: boolean) => void>();

/** Look up the toggleItem method for an accordion by its UID. */
export const getAccordionToggle = (accordionUid: string | number) => {
  return accordionToggleRegistry.get(accordionUid);
};

export interface UseAccordionOptions {
  props: Readonly<AccordionProps>;
}

/** Vertical-orientation part-radius classes for grouped accordion items. */
const PART_RADIUS_CLASSES = [
  "part-[base]:rounded-t-none!",
  "part-[base]:rounded-b-none!",
  "part-[base]:rounded-none!",
] as const;

/** Apply group positioning CSS classes and data attributes to accordion items. */
const applyGroupPositioning = (host: Element) => {
  const items = [...host.querySelectorAll(":scope > e-accordion-item")];
  for (const [index, item] of items.entries()) {
    const isFirst = index === 0;
    const isLast = index === items.length - 1;

    item.setAttribute("block", "true");
    setDataAttr(item, "groupOrientation", "vertical");
    setDataAttr(item, "groupSize", "sm");

    item.classList.toggle("group-first", isFirst);
    item.classList.toggle("group-last", isLast);
    item.classList.toggle("group-between", !isFirst && !isLast);

    item.classList.remove(...PART_RADIUS_CLASSES);
    if (isFirst && isLast) continue;
    if (isFirst) {
      item.classList.add("part-[base]:rounded-b-none!");
    } else if (isLast) {
      item.classList.add("part-[base]:rounded-t-none!");
    } else {
      item.classList.add("part-[base]:rounded-none!");
    }
  }
};

/** Watch for children to register, apply openItems once, then stop. */
const applyOpenItems = (
  props: Readonly<AccordionProps>,
  contextUid: string | number,
  stateStore: ReturnType<typeof useStateStore<AccordionItemProps>>,
): void => {
  const stopWatch = watch(
    () => stateStore.findChildrenByType<AccordionItemProps>(contextUid, ACCORDION_ITEM_TYPE),
    (children) => {
      if (children.length === 0) return;
      for (const child of children) {
        if (child.props.value && props.openItems!.includes(child.props.value)) {
          stateStore.setValue<AccordionItemProps>(child.uid as number, "open", true);
        }
      }
      nextTick(() => stopWatch());
    },
    { immediate: true, flush: "post" },
  );
};

/** Set up group positioning and slotchange listener. Returns cleanup function. */
const setupGroupLifecycle = (host: Element): (() => void) => {
  applyGroupPositioning(host);
  const slot = host.shadowRoot?.querySelector("slot") as HTMLSlotElement | null;
  const handler = () => applyGroupPositioning(host);
  slot?.addEventListener("slotchange", handler);
  return () => slot?.removeEventListener("slotchange", handler);
};

export const useAccordion = (options: UseAccordionOptions) => {
  const { props } = options;
  const host = useHost();
  useSharedState(props);

  const context = useComponentContext<AccordionProps>();
  const stateStore = useStateStore<AccordionItemProps>();

  const classes = computed(() => mergeClassProp(accordionRootClasses({ group: props.group }), props.class));

  const toggleItem = (itemUid: string | number, newOpen: boolean) => {
    if (newOpen && !props.multiple) {
      const siblings = stateStore.findChildrenByType<AccordionItemProps>(context.uid!, ACCORDION_ITEM_TYPE);
      for (const sibling of siblings) {
        if (sibling.uid === itemUid) continue;
        stateStore.setValue<AccordionItemProps>(sibling.uid as number, "open", false);
      }
    }
  };

  if (context.uid != null) accordionToggleRegistry.set(context.uid, toggleItem);
  if (props.openItems?.length && context.uid != null) applyOpenItems(props, context.uid, stateStore);

  let cleanupGroup: (() => void) | undefined;

  onMounted(() => {
    if (host && props.group) cleanupGroup = setupGroupLifecycle(host);
  });

  onBeforeUnmount(() => {
    if (context.uid != null) accordionToggleRegistry.delete(context.uid);
    cleanupGroup?.();
  });

  return { classes };
};
